import { Download, FileText, Award } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';

interface CertificatePreviewProps {
    certificateImage: string;
}

export function CertificatePreview({ certificateImage }: CertificatePreviewProps) {
    const handleDownload = () => {
        // Create a download link for the certificate
        const link = document.createElement('a');
        link.href = certificateImage;
        link.download = 'CHEP_Sustainability_Certificate.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-start gap-6">
                {/* Certificate Preview Thumbnail */}
                <div className="flex-shrink-0">
                    <div className="relative w-64 h-40 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-all duration-200 cursor-pointer group">
                        <img
                            src={certificateImage}
                            alt="CHEP Sustainability Certificate Preview"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-200" />
                    </div>
                </div>

                {/* Certificate Info and Actions */}
                <div className="flex-1 flex flex-col justify-between min-h-[160px]">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Award className="w-5 h-5 text-[#0072CE]" />
                            <h3 className="text-gray-900">Sustainability Certificate</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Official CHEP sustainability certificate recognizing contributions to the circular economy
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                <span>November 2024 - October 2025</span>
                            </div>
                        </div>
                    </div>

                    {/* Download Button */}
                    <div className="flex items-center gap-3 mt-4">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        onClick={handleDownload}
                                        className="bg-[#0072CE] hover:bg-[#0052a3] text-white gap-2"
                                    >
                                        <Download className="w-4 h-4" />
                                        Download Certificate
                                    </Button>
                                </TooltipTrigger>
                                {/* <TooltipContent className="max-w-[300px]">
                                    <p className="mb-2">🎉 <strong>Celebrate your achievement!</strong></p>
                                    <p className="text-sm">
                                        Click to instantly download your certificate and a ready-to-share post:
                                        Highlight your accomplishment with pride and inspire positive action.
                                    </p>
                                </TooltipContent> */}
                            </Tooltip>
                        </TooltipProvider>
                        <div className="text-xs text-gray-500">
                            High-resolution PNG format
                        </div>
                    </div>
                </div>
            </div>

            {/* Certificate Stats Bar */}
            <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                        <div className="text-2xl text-green-600 mb-1">1,458,669</div>
                        <div className="text-xs text-gray-600">Pounds CO₂ Saved</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl text-green-600 mb-1">25,271</div>
                        <div className="text-xs text-gray-600">Board Feet Reduced</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl text-green-600 mb-1">143,812</div>
                        <div className="text-xs text-gray-600">Pounds Waste Reduced</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
